import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Autocomplete, Box, Chip, CircularProgress, Divider, InputAdornment, Paper, TextField, Typography,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentsIcon from '@mui/icons-material/Payments';
import LockIcon from '@mui/icons-material/Lock';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import AddIcon from '@mui/icons-material/Add';
import { useDebounce } from '../../shared/hooks';
import { ContaBancariaService, IListagemConta, TTipoContaFinanceira } from '../services/api/cadastros/ContaBancaria';

interface IAutoCompleteContaConciliacaoProps {
  contaSelecionadaId: string;
  contaSelecionadaLabel: string;
  contaSelecionadaTipo?: TTipoContaFinanceira;
  aoSelecionarConta: (idconta: string, descricao: string, tipo?: TTipoContaFinanceira) => void;
}

// PIX não tem um `tipo` próprio na tabela (é uma conta 'recebiveis' como
// qualquer maquininha) — só dá pra diferenciar o ícone olhando o nome da
// conta. Heurística best-effort, não afeta o chip nem o filtro (esses
// continuam por `tipo` real).
const ehContaPix = (conta: IListagemConta) => conta.tipo === 'recebiveis' && conta.titular.toLowerCase().includes('pix');

const ICONE_POR_TIPO: Record<TTipoContaFinanceira, React.ReactElement> = {
  banco: <AccountBalanceIcon fontSize="small" />,
  caixa: <PaymentsIcon fontSize="small" />,
  cofre: <LockIcon fontSize="small" />,
  recebiveis: <CreditCardIcon fontSize="small" />,
};

const iconeDaConta = (conta: IListagemConta) => {
  if (!conta.tipo) return <AccountBalanceIcon fontSize="small" />;
  if (ehContaPix(conta)) return <QrCode2Icon fontSize="small" />;
  return ICONE_POR_TIPO[conta.tipo];
};

const LABEL_POR_TIPO: Record<TTipoContaFinanceira, string> = {
  banco: 'Banco',
  caixa: 'Caixa',
  cofre: 'Cofre',
  recebiveis: 'Recebíveis',
};

const COR_POR_TIPO: Record<TTipoContaFinanceira, string> = {
  banco: '#2563EB',
  caixa: '#22C55E',
  cofre: '#F97316',
  recebiveis: '#8B5CF6',
};

const OPCOES_FILTRO_TIPO: { valor: TTipoContaFinanceira | 'todos'; label: string }[] = [
  { valor: 'todos', label: 'Todas' },
  { valor: 'banco', label: 'Banco' },
  { valor: 'caixa', label: 'Caixa' },
  { valor: 'cofre', label: 'Cofre' },
  { valor: 'recebiveis', label: 'Recebíveis' },
];

// Descrição (segunda linha, cinza) — não existe um campo próprio pra isso na
// tabela, então usa o nome do banco quando existe (só é preenchido pra
// tipo='banco') e cai pro nome genérico do tipo nos outros casos.
const descricaoDaConta = (conta: IListagemConta) => {
  if (conta.tipo === 'banco' && conta.nomebanco) return conta.nomebanco;
  if (conta.tipo === 'recebiveis') return ehContaPix(conta) ? 'PIX Dinâmico' : 'Recebíveis';
  return conta.tipo ? LABEL_POR_TIPO[conta.tipo] : undefined;
};

// Agência/C-C (terceira linha, cinza) — mostrado pra qualquer tipo que tenha
// os dois preenchidos, não só banco (uma conta de recebíveis também pode ter
// dados bancários da maquininha cadastrados).
const dadosBancariosDaConta = (conta: IListagemConta) => {
  if (!conta.agencia || !conta.numeroconta) return undefined;
  return `Agência: ${conta.agencia} • C/C: ${conta.numeroconta}`;
};

// Autocomplete único de conta (banco/caixa/cofre/recebíveis), usado na tela
// de Conciliação — substitui a antiga sidebar com lista fixa de contas.
// Todos os tipos aparecem juntos na mesma lista (sem agrupamento), com o
// tipo indicado pelo ícone + chip de cada linha; um filtro por tipo no topo
// do dropdown restringe a lista quando necessário. Selecionar uma conta já
// dispara a busca dos lançamentos (ver Conciliacao.tsx/handleSelecionarConta),
// sem precisar clicar no ícone de pesquisa.
export const AutoCompleteContaConciliacao: React.FC<IAutoCompleteContaConciliacaoProps> = ({
  contaSelecionadaId,
  contaSelecionadaLabel,
  contaSelecionadaTipo,
  aoSelecionarConta,
}) => {
  const navigate = useNavigate();
  const { debounce } = useDebounce();
  const [open, setOpen] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtroTipo, setFiltroTipo] = useState<TTipoContaFinanceira | 'todos'>('todos');
  const [opcoes, setOpcoes] = useState<IListagemConta[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Prévia rápida pra escolher a conta, não uma listagem completa; quem não
  // achar digita pra refinar a busca ou usa o filtro por tipo.
  const LIMITE = 20;

  useEffect(() => {
    if (!open) return;
    setIsLoading(true);
    debounce(() => {
      ContaBancariaService
        .getAllList(busca, '', filtroTipo === 'todos' ? undefined : filtroTipo, LIMITE)
        .then((resultado) => {
          setIsLoading(false);
          setOpcoes(resultado instanceof Error ? [] : resultado.data);
        });
    });
  }, [busca, open, filtroTipo]);

  // Volta o filtro pra "Todas" toda vez que o dropdown fecha, pra não deixar
  // um filtro escondido confundindo a próxima vez que o usuário abrir.
  useEffect(() => {
    if (!open) setFiltroTipo('todos');
  }, [open]);

  const opcaoSelecionada = useMemo<IListagemConta | null>(
    () => (contaSelecionadaId
      ? { idconta: contaSelecionadaId, titular: contaSelecionadaLabel, status: true, tipo: contaSelecionadaTipo }
      : null),
    [contaSelecionadaId, contaSelecionadaLabel, contaSelecionadaTipo],
  );

  return (
    <Autocomplete
      size="small"
      fullWidth
      sx={{ minWidth: 240 }}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      // Dropdown mais largo que o campo — por padrão o Autocomplete encolhe
      // a lista pra largura do input, e ela é estreita demais (~260px) pra
      // mostrar nome + subtítulo + chip sem cortar.
      componentsProps={{ popper: { style: { width: 440 }, placement: 'bottom-start' } }}
      options={opcoes}
      loading={isLoading}
      value={opcaoSelecionada}
      getOptionLabel={(opcao) => opcao.titular}
      isOptionEqualToValue={(opcao, val) => opcao.idconta === val.idconta}
      filterOptions={(x) => x}
      onInputChange={(_, novoValor, reason) => {
        if (reason === 'input') setBusca(novoValor);
      }}
      onChange={(_, novaConta) => aoSelecionarConta(novaConta?.idconta ?? '', novaConta?.titular ?? '', novaConta?.tipo)}
      noOptionsText={busca.length < 2 ? 'Digite para buscar' : 'Nenhuma conta encontrada'}
      renderOption={(props, opcao) => (
        <Box component="li" {...props} key={opcao.idconta} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
          <Box sx={{ color: opcao.tipo ? COR_POR_TIPO[opcao.tipo] : 'text.secondary', display: 'flex', mt: 0.25 }}>
            {iconeDaConta(opcao)}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="body2" fontWeight={600} noWrap>{opcao.titular}</Typography>
              {opcao.tipo && (
                <Chip
                  label={LABEL_POR_TIPO[opcao.tipo].toUpperCase()}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    flexShrink: 0,
                    color: COR_POR_TIPO[opcao.tipo],
                    bgcolor: `${COR_POR_TIPO[opcao.tipo]}1a`,
                  }}
                />
              )}
            </Box>
            {descricaoDaConta(opcao) && (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {descricaoDaConta(opcao)}
              </Typography>
            )}
            {dadosBancariosDaConta(opcao) && (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {dadosBancariosDaConta(opcao)}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      PaperComponent={(paperProps) => (
        <Paper {...paperProps}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, px: 1.5, py: 1 }}>
            {OPCOES_FILTRO_TIPO.map((opcao) => (
              <Chip
                key={opcao.valor}
                label={opcao.label}
                size="small"
                clickable
                variant={filtroTipo === opcao.valor ? 'filled' : 'outlined'}
                color={filtroTipo === opcao.valor ? 'primary' : 'default'}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setFiltroTipo(opcao.valor)}
              />
            ))}
          </Box>
          <Divider />
          {paperProps.children}
          <Divider />
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25,
              cursor: 'pointer', color: 'primary.main', '&:hover': { bgcolor: 'action.hover' },
            }}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setOpen(false); navigate('/contabancaria'); }}
          >
            <AddIcon fontSize="small" />
            <Typography variant="body2" fontWeight={600}>Gerenciar contas</Typography>
          </Box>
        </Paper>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Conta"
          placeholder="Buscar conta..."
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                {opcaoSelecionada?.tipo ? iconeDaConta(opcaoSelecionada) : <AccountBalanceIcon fontSize="small" />}
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
